import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { WidgetService } from '../services/widget.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { TagItemList } from '../../campaign/widgets.model';

const TAG_NAME_PATTERN = /^[a-zA-Z0-9 _-]{1,64}$/;

const KEYS = {
  enterName: 'shared.tag-selector.confirm-disabled',
  tooLong: 'shared.tag-selector.error.too-long',
  invalidChars: 'shared.tag-selector.error.invalid-chars',
  duplicate: 'shared.tag-selector.error.duplicate',
  createFailed: 'shared.tag-selector.error.create-failed',
} as const;

@Component({
  selector: 'app-tag-selector',
  templateUrl: './tag-selector.component.html',
  styleUrl: './tag-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TagSelectorComponent implements OnInit {
  private readonly widgetService = inject(WidgetService);
  private readonly snackbar = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);
  readonly selectedTag = model<string | null>(null);
  readonly tags = input<string[] | null>(null);
  readonly allowCreate = input(true);
  readonly allowNone = input(false);
  readonly tagCreated = output<string>();

  protected availableTags = signal<string[]>([]);
  protected isCreating = signal(false);
  protected isSaving = signal(false);

  constructor() {
    effect(() => {
      const external = this.tags();
      if (external !== null) {
        this.availableTags.set(external);
      }
    });
  }

  private duplicateTagValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const trimmed = (control.value ?? '').trim();
      return trimmed && this.availableTags().some((t) => t.toLowerCase() === trimmed.toLowerCase())
        ? { duplicate: true }
        : null;
    };
  }

  protected readonly newTagControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(64),
    Validators.pattern(TAG_NAME_PATTERN),
    this.duplicateTagValidator(),
  ]);

  private readonly errorKey = computed<string | null>(() => {
    void this._newTagValueSignal();
    const c = this.newTagControl;
    if ((c.value ?? '').trim().length === 0) return KEYS.enterName;
    if (c.hasError('maxlength')) return KEYS.tooLong;
    if (c.hasError('pattern')) return KEYS.invalidChars;
    if (c.hasError('duplicate')) return KEYS.duplicate;
    return null;
  });

  protected validationErrorKey = computed<string | null>(() => (this._dirtySignal() ? this.errorKey() : null));

  protected confirmDisabledTooltipKey = computed<string>(() => this.errorKey() ?? KEYS.enterName);

  protected canConfirm = computed(() => {
    void this._newTagValueSignal();
    return this.newTagControl.valid && !this.isSaving();
  });

  private _newTagValueSignal = signal('');
  private _dirtySignal = signal(false);

  ngOnInit(): void {
    if (this.tags() === null) {
      this.loadTags();
    }

    this.newTagControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this._newTagValueSignal.set(val ?? '');
      if (this.newTagControl.dirty) {
        this._dirtySignal.set(true);
      }
    });
  }

  private loadTags(): void {
    this.widgetService
      .getAllTags()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.availableTags.set(result.data));
  }

  protected startCreating(): void {
    this.newTagControl.reset('');
    this._dirtySignal.set(false);
    this.isCreating.set(true);
  }

  protected cancelCreating(): void {
    this.isCreating.set(false);
    this.newTagControl.reset('');
    this._dirtySignal.set(false);
  }

  protected confirmCreate(): void {
    this.newTagControl.markAsDirty();
    this._dirtySignal.set(true);
    if (!this.canConfirm()) return;

    const name = (this.newTagControl.value ?? '').trim();
    this.isSaving.set(true);

    const request: TagItemList = { name, organizations: null, type: 0 };

    this.widgetService
      .createListTag(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.availableTags.update((list) => [...list, name].sort((a, b) => a.localeCompare(b)));
          this.tagCreated.emit(name);
          this.selectedTag.set(name);
          this.isCreating.set(false);
          this.newTagControl.reset('');
          this.isSaving.set(false);
        },
        error: () => {
          this.snackbar.error(KEYS.createFailed);
          this.isSaving.set(false);
        },
      });
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmCreate();
    }
    if (event.key === 'Escape') {
      this.cancelCreating();
    }
  }
}
